import type { Dict } from "../types";

export const roomsEquipment: Dict = {
  en: {
    "BookEquipment.label": "Equipment",
    "BookEquipment.labelOptional": "Equipment (optional)",
    "BookEquipment.placeholder": "Choose a device",
    "BookEquipment.errorRequired": "Choose a device for this service.",
    "BookEquipment.errorAllBusy": "No device is free in this slot. Pick another time.",
    "BookEquipment.stillLoading": "Available devices are still loading. Try again in a moment.",
    "BookEquipment.loadFailed": "The list of devices could not be read. Check your connection, then try again.",
    "BookEquipment.assignFailedTitle": "Appointment created, no room assigned",
    "BookEquipment.assignFailedBody": "the room or the device could not be reserved. Open the appointment to assign one.",
    "BookEquipment.reason.roomBlocked": "room closed",
    "BookEquipment.reason.roomFull": "room full",
    "BookEquipment.reason.roomUnavailable": "room inactive",
    "BookEquipment.reason.roomNotForService": "room not set up for this service",
    "BookEquipment.reason.equipmentInactive": "device removed",
    "BookEquipment.reason.equipmentNotInRoom": "not in this room",
    "BookEquipment.reason.equipmentBusyInRoom": "already in use",
    "BookEquipment.reason.equipmentAllUnitsInUse": "all units are taken",
    "BookEquipment.reason.unavailable": "unavailable",
    "RoomsEquipment.headerSubtitle": "Rooms, equipment and the services performed in them.",
    "RoomsEquipment.tileActiveRooms": "Active rooms",
    "RoomsEquipment.tileWorkstations": "Workstations",
    "RoomsEquipment.tileEquipment": "Equipment",
    "RoomsEquipment.tileCoveredServices": "Covered services",

    // Rooms & Equipment
    "RoomsEquipment.title": "Rooms & Equipment",
    "RoomsEquipment.rooms": "Rooms",
    "RoomsEquipment.equipment": "Equipment",
    "RoomsEquipment.addRoom": "Add Room",
    "RoomsEquipment.editRoom": "Edit Room",
    "RoomsEquipment.deleteRoom": "Delete Room",
    "RoomsEquipment.roomName": "Room Name",
    "RoomsEquipment.workstations": "Workstations",
    "RoomsEquipment.workstationsDesc": "Number of workstations in this room",
    "RoomsEquipment.workstationsOne": "workstation",
    "RoomsEquipment.workstationsMany": "workstations",
    "RoomsEquipment.workstationsMinimum": "A room has at least one workstation.",
    "RoomsEquipment.nameRequired": "Enter a name before saving.",
    "RoomsEquipment.fixedEquipment": "Fixed Equipment",
    "RoomsEquipment.compatibleMovableEquipment": "Compatible Movable Equipment",
    "RoomsEquipment.addEquipment": "Add Equipment",
    "RoomsEquipment.editEquipment": "Edit Equipment",
    "RoomsEquipment.deleteEquipment": "Delete Equipment",
    "RoomsEquipment.equipmentName": "Equipment Name",
    "RoomsEquipment.equipmentType": "Type",
    "RoomsEquipment.fixed": "Fixed",
    "RoomsEquipment.movable": "Movable",
    "RoomsEquipment.quantity": "Quantity",
    "RoomsEquipment.compatibleRooms": "Compatible Rooms",
    "RoomsEquipment.installedIn": "Installed in",
    "RoomsEquipment.noRooms": "No rooms yet. Add rooms to manage your clinic's spaces.",
    "RoomsEquipment.noEquipment": "No equipment yet. Add equipment to track availability.",
    "RoomsEquipment.active": "Active",
    "RoomsEquipment.inactive": "Inactive",
    "RoomsEquipment.confirmDeleteRoomNamed": "Delete the room {name}?",
    "RoomsEquipment.deleteImpactUpcomingOne": "{count} upcoming appointment will lose its room.",
    "RoomsEquipment.deleteImpactUpcomingMany": "{count} upcoming appointments will lose their room.",
    "RoomsEquipment.deleteImpactPastOne": "{count} past appointment will lose its room in reports.",
    "RoomsEquipment.deleteImpactPastMany": "{count} past appointments will lose their room in reports.",
    "RoomsEquipment.deleteImpactUnavailable": "Unable to tell how many appointments will be affected.",
    "RoomsEquipment.confirmDeleteEquipmentNamed": "Delete the equipment {name}?",
    "RoomsEquipment.deleteImpactRooms": "It will be removed from {rooms}.",
    "RoomsEquipment.confirmTypeChangeTitle": "Change this equipment type?",
    "RoomsEquipment.confirmTypeChangeBody": "{name} is attached to {rooms}. Switching it to {type} will move those attachments.",
    "RoomsEquipment.confirmTypeChangeTitleRepair": "Put these attachments back in place?",
    "RoomsEquipment.confirmTypeChangeBodyRepair": "{name} is already {type}, but attachments for {rooms} were left on the old type. They will be put back where they belong.",
    "RoomsEquipment.equipmentScopeNotice": "This choice applies to every room where this service is offered.",
    "RoomsEquipment.equipmentMissingWarningOne": "{service} is also offered in {rooms}, which does not have this equipment. Booking will never place this service there.",
    "RoomsEquipment.equipmentMissingWarningMany": "{service} is also offered in {rooms}, which do not have this equipment. Booking will never place this service there.",
    "RoomsEquipment.makeRoomsCompatibleOne": "Make this room compatible",
    "RoomsEquipment.makeRoomsCompatibleMany": "Make these rooms compatible",
    "RoomsEquipment.declareEquipmentInstalledOne": "Mark the equipment as installed in this room",
    "RoomsEquipment.declareEquipmentInstalledMany": "Mark the equipment as installed in these rooms",
    "RoomsEquipment.removeServiceFromRoomsOne": "Remove the service from this room",
    "RoomsEquipment.removeServiceFromRoomsMany": "Remove the service from these rooms",
    "RoomsEquipment.removeServiceAlsoClearsEquipmentOne": "This is the only room where this service is offered. Removing it will also clear the equipment required by the service.",
    "RoomsEquipment.removeServiceAlsoClearsEquipmentMany": "These are the only rooms where this service is offered. Removing it will also clear the equipment required by the service.",
    "RoomsEquipment.priceRange": "{min} to {max}",
    "RoomsEquipment.roomSaved": "Room saved successfully",
    "RoomsEquipment.roomDeleted": "Room deleted successfully",
    "RoomsEquipment.equipmentSaved": "Equipment saved successfully",
    "RoomsEquipment.equipmentDeleted": "Equipment deleted successfully",
    "RoomsEquipment.requiresRoom": "Requires a room",
    "RoomsEquipment.requiresRoomDesc": "Assign a room when booking this service",
    "RoomsEquipment.requiredEquipment": "Required equipment",
    "RoomsEquipment.none": "None",
    "RoomsEquipment.noRoomAvailable": "No room available at this time",
    "RoomsEquipment.assignedRoom": "Room",
    "RoomsEquipment.roomColor": "Room Color",
    "RoomsEquipment.roomColorHint": "Custom color for calendar display. Leave empty for automatic color.",
    "RoomsEquipment.resetColor": "Reset",
    "RoomsEquipment.eligibleRooms": "Eligible Rooms",
    "RoomsEquipment.eligibleRoomsDesc": "Select which rooms this service can be performed in. If none selected, any room is eligible.",
    "RoomsEquipment.noRoomAvailableWarning": "No room available",
    "RoomsEquipment.noRoomAvailableWarningDesc": "No room available for: {{services}}. Do you still want to create the appointment?",
    "RoomsEquipment.createAnyway": "Create anyway",
    "RoomsEquipment.noAccessTitle": "No access",
    "RoomsEquipment.noAccessRoomsDescription": "You do not have permission to view rooms. Contact the salon owner to request access.",
    "RoomsEquipment.noAccessEquipmentDescription": "You do not have permission to view equipment. Contact the salon owner to request access.",
    "RoomsEquipment.viewCards": "Cards",
    "RoomsEquipment.viewMatrix": "Matrix",
    "RoomsEquipment.servicesHere": "Services available here",
    "RoomsEquipment.servicesSavedLive": "Changes are saved as you go.",
    "RoomsEquipment.roomDialogDescription": "A room is a space where appointments take place. The workstation count is how many clients can be served there at the same time.",
    "RoomsEquipment.equipmentDialogDescription": "Fixed equipment stays in one room. Movable equipment travels between rooms, and the quantity is how many units you own.",
    "RoomsEquipment.servicesDialogDescription": "Tick the services that can be performed in this room. Online booking will only offer this room for those services.",
    "RoomsEquipment.editServices": "Services",
    "RoomsEquipment.noServicesYet": "No services yet",
    "RoomsEquipment.noRequiredEquipment": "No required equipment",
    "RoomsEquipment.noSection": "No section",
    "RoomsEquipment.variableDuration": "variable duration",
    "RoomsEquipment.variablePrice": "variable price",
    "RoomsEquipment.startingFrom": "from",
    "RoomsEquipment.minutesShort": "min",
    "RoomsEquipment.mobilePool": "Mobile equipment",
    "RoomsEquipment.assignToRooms": "Assign to rooms",
    "RoomsEquipment.fixedPool": "Fixed equipment",
    "RoomsEquipment.installInRooms": "Install in these rooms",
    "RoomsEquipment.unitPlaced": "unit placed",
    "RoomsEquipment.unitsPlaced": "units placed",
    "RoomsEquipment.allUnitsPlaced": "All units are placed. Remove one to free a room.",
    "RoomsEquipment.toast.fixedCapReached": "This equipment does not have enough units to be installed in one more room. Increase its quantity in the Equipment tab.",
    "RoomsEquipment.serviceVsRoom": "Service \\ Room",
    "RoomsEquipment.matrixEmpty": "Add services and rooms to configure the matrix.",
    "RoomsEquipment.undeclaredTitle": "Undeclared equipment",
    "RoomsEquipment.undeclaredBody": "These services take place in an equipped room but require no device. Online booking will not reserve the device for them, and it may be double booked.",
    "RoomsEquipment.undeclaredFix": "Declare the required device",
    "RoomsEquipment.inactiveSuffix": "(inactive)",
    "RoomsEquipment.noRoomConfigured": "No room configured",
    "RoomsEquipment.manage": "Manage",
    "RoomsEquipment.noRoom": "No room",
    "RoomsEquipment.roomReassigned": "Room updated",
    "RoomsEquipment.warning.occupied": "Workstation occupied at this time (forced)",
    "RoomsEquipment.warning.equipment": "Required equipment unavailable (forced)",
    "RoomsEquipment.warning.mismatch": "Room not ideal for this service (forced)",
  },
  fr: {
    "RoomsEquipment.headerSubtitle": "Salles, matériel et services qui s'y déroulent.",
    "RoomsEquipment.tileActiveRooms": "Salles actives",
    "RoomsEquipment.tileWorkstations": "Postes",
    "RoomsEquipment.tileEquipment": "Équipements",
    "RoomsEquipment.tileCoveredServices": "Services couverts",
    "BookEquipment.label": "Équipement",
    "BookEquipment.labelOptional": "Équipement (facultatif)",
    "BookEquipment.placeholder": "Choisissez un appareil",
    "BookEquipment.errorRequired": "Choisissez un appareil pour ce service.",
    "BookEquipment.errorAllBusy": "Aucun appareil n'est libre sur ce créneau. Choisissez une autre heure.",
    "BookEquipment.stillLoading": "Les appareils disponibles se chargent. Réessayez dans un instant.",
    "BookEquipment.loadFailed": "La liste des appareils n'a pas pu être lue. Vérifiez votre connexion, puis réessayez.",
    "BookEquipment.assignFailedTitle": "Rendez-vous créé, salle non assignée",
    "BookEquipment.assignFailedBody": "la salle ou l'appareil n'a pas pu être réservé. Ouvrez le rendez-vous pour en assigner un.",
    "BookEquipment.reason.roomBlocked": "salle fermée",
    "BookEquipment.reason.roomFull": "salle complète",
    "BookEquipment.reason.roomUnavailable": "salle inactive",
    "BookEquipment.reason.roomNotForService": "salle non prévue pour ce service",
    "BookEquipment.reason.equipmentInactive": "appareil retiré",
    "BookEquipment.reason.equipmentNotInRoom": "absent de cette salle",
    "BookEquipment.reason.equipmentBusyInRoom": "déjà utilisé",
    "BookEquipment.reason.equipmentAllUnitsInUse": "toutes les unités sont prises",
    "BookEquipment.reason.unavailable": "indisponible",

    // Rooms & Equipment
    "RoomsEquipment.title": "Salles & Équipements",
    "RoomsEquipment.rooms": "Salles",
    "RoomsEquipment.equipment": "Équipements",
    "RoomsEquipment.addRoom": "Ajouter une salle",
    "RoomsEquipment.editRoom": "Modifier la salle",
    "RoomsEquipment.deleteRoom": "Supprimer la salle",
    "RoomsEquipment.roomName": "Nom de la salle",
    "RoomsEquipment.workstations": "Postes de travail",
    "RoomsEquipment.workstationsDesc": "Nombre de postes de travail dans cette salle",
    "RoomsEquipment.workstationsOne": "poste de travail",
    "RoomsEquipment.workstationsMany": "postes de travail",
    "RoomsEquipment.workstationsMinimum": "Une salle compte au moins un poste de travail.",
    "RoomsEquipment.nameRequired": "Donnez un nom avant d'enregistrer.",
    "RoomsEquipment.fixedEquipment": "Équipements fixes",
    "RoomsEquipment.compatibleMovableEquipment": "Équipements amovibles compatibles",
    "RoomsEquipment.addEquipment": "Ajouter un équipement",
    "RoomsEquipment.editEquipment": "Modifier l'équipement",
    "RoomsEquipment.deleteEquipment": "Supprimer l'équipement",
    "RoomsEquipment.equipmentName": "Nom de l'équipement",
    "RoomsEquipment.equipmentType": "Type",
    "RoomsEquipment.fixed": "Fixe",
    "RoomsEquipment.movable": "Amovible",
    "RoomsEquipment.quantity": "Quantité",
    "RoomsEquipment.compatibleRooms": "Salles compatibles",
    "RoomsEquipment.installedIn": "Installé dans",
    "RoomsEquipment.noRooms": "Aucune salle. Ajoutez des salles pour gérer vos espaces.",
    "RoomsEquipment.noEquipment": "Aucun équipement. Ajoutez des équipements pour suivre leur disponibilité.",
    "RoomsEquipment.active": "Active",
    "RoomsEquipment.inactive": "Inactive",
    "RoomsEquipment.confirmDeleteRoomNamed": "Êtes-vous sûr de vouloir supprimer la salle {name} ?",
    "RoomsEquipment.deleteImpactUpcomingOne": "{count} rendez-vous à venir perdra sa salle.",
    "RoomsEquipment.deleteImpactUpcomingMany": "{count} rendez-vous à venir perdront leur salle.",
    "RoomsEquipment.deleteImpactPastOne": "{count} rendez-vous passé perdra la trace de sa salle dans les rapports.",
    "RoomsEquipment.deleteImpactPastMany": "{count} rendez-vous passés perdront la trace de leur salle dans les rapports.",
    "RoomsEquipment.deleteImpactUnavailable": "Impossible de savoir combien de rendez-vous seront touchés.",
    "RoomsEquipment.confirmDeleteEquipmentNamed": "Êtes-vous sûr de vouloir supprimer l'équipement {name} ?",
    "RoomsEquipment.deleteImpactRooms": "Il sera retiré de {rooms}.",
    "RoomsEquipment.confirmTypeChangeTitle": "Changer le type de cet équipement ?",
    "RoomsEquipment.confirmTypeChangeBody": "{name} est rattaché à {rooms}. En le passant en {type}, ces rattachements seront déplacés.",
    "RoomsEquipment.confirmTypeChangeTitleRepair": "Remettre ces rattachements en place ?",
    "RoomsEquipment.confirmTypeChangeBodyRepair": "{name} est déjà {type}, mais des rattachements pour {rooms} sont restés du côté de l'ancien type. Ils vont être remis en place.",
    "RoomsEquipment.equipmentScopeNotice": "Ce choix vaut pour toutes les salles où ce service est offert.",
    "RoomsEquipment.equipmentMissingWarningOne": "Le service {service} est aussi offert dans {rooms}, qui n'a pas cet équipement. La réservation n'y placera jamais ce service.",
    "RoomsEquipment.equipmentMissingWarningMany": "Le service {service} est aussi offert dans {rooms}, qui n'ont pas cet équipement. La réservation n'y placera jamais ce service.",
    "RoomsEquipment.makeRoomsCompatibleOne": "Rendre cette salle compatible",
    "RoomsEquipment.makeRoomsCompatibleMany": "Rendre ces salles compatibles",
    "RoomsEquipment.declareEquipmentInstalledOne": "Déclarer l'équipement installé dans cette salle",
    "RoomsEquipment.declareEquipmentInstalledMany": "Déclarer l'équipement installé dans ces salles",
    "RoomsEquipment.removeServiceFromRoomsOne": "Retirer le service de cette salle",
    "RoomsEquipment.removeServiceFromRoomsMany": "Retirer le service de ces salles",
    "RoomsEquipment.removeServiceAlsoClearsEquipmentOne": "Cette salle est la seule où ce service est offert. Le retirer effacera aussi l'équipement requis par le service.",
    "RoomsEquipment.removeServiceAlsoClearsEquipmentMany": "Ces salles sont les seules où ce service est offert. Le retirer effacera aussi l'équipement requis par le service.",
    "RoomsEquipment.priceRange": "{min} à {max}",
    "RoomsEquipment.roomSaved": "Salle enregistrée avec succès",
    "RoomsEquipment.roomDeleted": "Salle supprimée avec succès",
    "RoomsEquipment.equipmentSaved": "Équipement enregistré avec succès",
    "RoomsEquipment.equipmentDeleted": "Équipement supprimé avec succès",
    "RoomsEquipment.requiresRoom": "Nécessite une salle",
    "RoomsEquipment.requiresRoomDesc": "Attribuer une salle lors de la réservation de ce service",
    "RoomsEquipment.requiredEquipment": "Équipement requis",
    "RoomsEquipment.none": "Aucun",
    "RoomsEquipment.noRoomAvailable": "Aucune salle disponible à ce créneau",
    "RoomsEquipment.assignedRoom": "Salle",
    "RoomsEquipment.roomColor": "Couleur de la salle",
    "RoomsEquipment.roomColorHint": "Couleur personnalisée pour l'affichage au calendrier. Laisser vide pour une couleur automatique.",
    "RoomsEquipment.resetColor": "Réinitialiser",
    "RoomsEquipment.eligibleRooms": "Salles éligibles",
    "RoomsEquipment.eligibleRoomsDesc": "Sélectionner dans quelles salles ce service peut être effectué. Si aucune n'est sélectionnée, toute salle est éligible.",
    "RoomsEquipment.noRoomAvailableWarning": "Aucune salle disponible",
    "RoomsEquipment.noRoomAvailableWarningDesc": "Aucune salle disponible pour : {{services}}. Voulez-vous quand même créer le rendez-vous ?",
    "RoomsEquipment.createAnyway": "Créer quand même",
    "RoomsEquipment.noAccessTitle": "Accès refusé",
    "RoomsEquipment.noAccessRoomsDescription": "Vous n'avez pas la permission de voir les salles. Contactez le propriétaire du salon pour demander l'accès.",
    "RoomsEquipment.noAccessEquipmentDescription": "Vous n'avez pas la permission de voir les équipements. Contactez le propriétaire du salon pour demander l'accès.",
    "RoomsEquipment.viewCards": "Cartes",
    "RoomsEquipment.viewMatrix": "Matrice",
    "RoomsEquipment.servicesHere": "Services possibles ici",
    "RoomsEquipment.servicesSavedLive": "Les changements sont enregistrés au fur et à mesure.",
    "RoomsEquipment.roomDialogDescription": "Une salle est un espace où se déroulent des rendez-vous. Le nombre de postes indique combien de clients peuvent y être servis en même temps.",
    "RoomsEquipment.equipmentDialogDescription": "Un équipement fixe reste dans une salle. Un équipement amovible se déplace d'une salle à l'autre, la quantité indique combien d'unités vous en possédez.",
    "RoomsEquipment.servicesDialogDescription": "Cochez les services qui peuvent être rendus dans cette salle. La réservation en ligne ne proposera cette salle que pour ces services.",
    "RoomsEquipment.editServices": "Services",
    "RoomsEquipment.noServicesYet": "Aucun service pour l'instant",
    "RoomsEquipment.noRequiredEquipment": "Aucun équipement requis",
    "RoomsEquipment.noSection": "Sans section",
    "RoomsEquipment.variableDuration": "durée variable",
    "RoomsEquipment.variablePrice": "prix variable",
    "RoomsEquipment.startingFrom": "à partir de",
    "RoomsEquipment.minutesShort": "min",
    "RoomsEquipment.mobilePool": "Équipements amovibles",
    "RoomsEquipment.assignToRooms": "Assigner aux salles",
    "RoomsEquipment.fixedPool": "Équipements fixes",
    "RoomsEquipment.installInRooms": "Installer dans ces salles",
    "RoomsEquipment.unitPlaced": "unité posée",
    "RoomsEquipment.unitsPlaced": "unités posées",
    "RoomsEquipment.allUnitsPlaced": "Toutes les unités sont posées. Retirez-en une pour libérer une salle.",
    "RoomsEquipment.toast.fixedCapReached": "Cet équipement n'a pas assez d'unités pour être installé dans une salle de plus. Augmentez sa quantité dans l'onglet Équipements.",
    "RoomsEquipment.serviceVsRoom": "Service \\ Salle",
    "RoomsEquipment.matrixEmpty": "Ajoutez des services et des salles pour configurer la matrice.",
    "RoomsEquipment.undeclaredTitle": "Appareil non déclaré",
    "RoomsEquipment.undeclaredBody": "Ces services se donnent dans une salle équipée mais n'exigent aucun appareil. La réservation en ligne ne réservera donc pas l'appareil pour eux, et il pourra être pris deux fois.",
    "RoomsEquipment.undeclaredFix": "Déclarer l'appareil requis",
    "RoomsEquipment.inactiveSuffix": "(inactive)",
    "RoomsEquipment.noRoomConfigured": "Aucune salle configurée",
    "RoomsEquipment.manage": "Gérer",
    "RoomsEquipment.noRoom": "Aucune salle",
    "RoomsEquipment.roomReassigned": "Salle modifiée",
    "RoomsEquipment.warning.occupied": "Poste occupé à cette heure (forcé)",
    "RoomsEquipment.warning.equipment": "Équipement requis indisponible (forcé)",
    "RoomsEquipment.warning.mismatch": "Salle peu adaptée à ce service (forcé)",
  },
};
